import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Clock, Users, TrendingUp, BarChart3, Zap, CheckCircle, ArrowRight, Target, DollarSign, FileBarChart, Play } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getAuthUrl } from "@/utils/redirectUtils";

const AgencyPage = () => {
  const problems = [
    {
      icon: <Clock className="h-8 w-8 text-red-500" />,
      title: "Drowning in Time-Consuming Tasks",
      description: "Spending 15+ hours per week on manual campaign creation and optimization instead of strategy."
    },
    {
      icon: <Users className="h-8 w-8 text-red-500" />,
      title: "Can't Scale Without Adding Staff",
      description: "Every new client requires hiring more people, killing your profit margins."
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-red-500" />,
      title: "Struggling to Prove ROI to Clients",
      description: "Manual reporting takes hours and doesn't clearly show the value you're delivering."
    },
  ];

  const benefits = [
    {
      icon: <Zap className="h-8 w-8 text-primary" />,
      title: "Client Budget Safety Net",
      description: "Pitch clients knowing you have a tool that mechanically prevents them from overspending on generic $50 clicks."
    },
    {
      icon: <Target className="h-8 w-8 text-primary" />,
      title: "Automate Campaign Creation",
      description: "Generate hundreds of high-converting campaigns instantly with automatically enforced target CPA ceilings."
    },
    {
      icon: <DollarSign className="h-8 w-8 text-primary" />,
      title: "Never Apologize for Wasted Spend",
      description: "Our AI blocks expensive, non-converting keywords before campaigns even launch, protecting your agency's reputation."
    },
    {
      icon: <FileBarChart className="h-8 w-8 text-primary" />,
      title: "Professional White-Label Reports",
      description: "Branded reports automatically generated weekly, showcasing your agency's strategic impact and ROI."
    },
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Connect Your Clients",
      description: "Securely link all client Google Ads accounts to your agency dashboard in under 5 minutes."
    },
    {
      step: "2",
      title: "AI Creates Campaigns",
      description: "Our AI analyzes client websites and generates optimized campaigns with perfect keyword targeting."
    },
    {
      step: "3",
      title: "Scale & Profit",
      description: "Manage unlimited clients without hiring. Focus on strategy while AI handles the execution."
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Google Ads Management for Digital Agencies | Upfixe</title>
        <meta name="description" content="Scale unlimited clients with AI-powered Google Ads automation. Generate campaigns in minutes, automate reporting, and 3x ROI. Perfect for agencies." />
        <meta name="keywords" content="Google Ads for agencies, agency campaign management, white-label Google Ads, scale agency" />
        <meta property="og:title" content="Google Ads Management for Digital Agencies | Upfixe" />
        <meta property="og:description" content="Scale unlimited clients with AI-powered Google Ads automation. Generate campaigns in minutes, automate reporting, and 3x ROI." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://upfixe.com/agency" />
      </Helmet>
      <Header />
      
      <main className="flex-grow pt-24 pb-16">
        {/* Hero Section */}
        <section className="py-16 px-4 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <Badge className="mb-6 text-sm font-medium">
                For Digital Marketing Agencies
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900">
                Scale Campaigns <span className="text-primary">Without Blowing Client Budgets</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
                Generate 100s of campaigns in minutes with built-in safety nets. Our AI filters out margin-killing keywords automatically, so your clients never complain about wasted spend.
              </p>
              
              <div className="flex justify-center mb-8">
                <Button 
                  size="lg" 
                  className="text-lg px-8 h-14 font-bold"
                  onClick={() => window.location.href = getAuthUrl()}
                >
                  Start Your Free Agency Account <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
              
              <p className="text-gray-500 text-sm">
                No credit card required • Free plan available • Setup in 5 minutes
              </p>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-16 px-4 bg-red-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                Are You Stuck in the Agency Trap?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Most agencies hit a wall when trying to scale. Sound familiar?
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {problems.map((problem, index) => (
                <Card key={index} className="border-red-200 bg-white">
                  <CardHeader>
                    <div className="mb-4">{problem.icon}</div>
                    <CardTitle className="text-lg">{problem.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{problem.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-16 px-4 bg-green-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <Badge className="mb-6 bg-green-100 text-green-700">
                The Solution
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                Finally, Agency Growth That Actually Works
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Upfixe Ads transforms your agency operations with AI that works like your best account manager—but 10x faster.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border-green-200 bg-white">
                  <CardHeader>
                    <div className="mb-4">{benefit.icon}</div>
                    <CardTitle className="text-xl">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Video Demo Section */}
        <section className="py-24 px-4 bg-gradient-to-br from-background to-muted/50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                See How Agencies Scale with Upfixe
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Watch how our AI handles complete client campaign management in minutes, not hours. 
                From client onboarding to campaign optimization—all automated.
              </p>
            </div>

            <div className="relative max-w-4xl mx-auto mb-12">
              <div className="aspect-video rounded-2xl overflow-hidden border-2 border-primary/20">
                <iframe
                  src="https://www.youtube.com/embed/ywhSSihPN_E"
                  title="Upfixe Agency Demo Video"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => window.location.href = getAuthUrl()}
                className="text-lg px-8"
              >
                Start Your Free Agency Account <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8"
                onClick={() => window.open('https://youtu.be/ywhSSihPN_E', '_blank')}
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Full Demo
              </Button>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Scale Your Agency in 3 Simple Steps
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Get your agency scaling in under 24 hours
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {howItWorks.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 rounded-full bg-primary text-white text-2xl font-bold flex items-center justify-center mx-auto mb-6">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold mb-4">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Supercharged Add-on */}
        <section className="py-16 px-4 bg-purple-50">
          <div className="container mx-auto max-w-5xl">
            <Card className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">
              <CardContent className="p-8">
                <div className="text-center">
                  <Badge className="mb-6 bg-white/20 text-white">
                    🚀 Supercharged Add-on
                  </Badge>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Generate 100+ Campaigns Instantly
                  </h2>
                  <p className="text-xl mb-8 opacity-90">
                    Perfect for agencies: Our AI scans client websites and bulk-generates hundreds of perfectly optimized campaigns across multiple countries and languages. What used to take weeks now takes minutes.
                  </p>
                  <Button size="lg" variant="secondary" className="text-purple-600 font-bold">
                    <Link to="/supercharged">See Supercharged in Action</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Blog Links Section */}
        <section className="py-20 px-4 bg-blue-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Learn More About Agency Growth</h2>
              <p className="text-lg text-muted-foreground">
                Read our guides on scaling your agency, reducing client CPA, and automating campaign management.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <a href="/blog/google-ads-for-agencies" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Google Ads for Agencies</h3>
                <p className="text-sm text-muted-foreground mb-4">Scale your client management 10x with automation. Manage unlimited accounts without hiring.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
              <a href="/blog/reduce-google-ads-cpa" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Reduce Google Ads CPA</h3>
                <p className="text-sm text-muted-foreground mb-4">Help your clients cut cost-per-acquisition in half with 7 proven strategies.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-8">
              Ready to 3x Your Agency Without Hiring?
            </h2>
            <p className="text-xl mb-12 opacity-90">
              Start Your Free Account and scale your agency profitably.
            </p>
            
            <div className="flex justify-center mb-8">
              <Button 
                size="lg" 
                className="text-lg px-8 h-14 bg-white text-blue-600 hover:bg-blue-50 font-bold"
                onClick={() => window.location.href = getAuthUrl()}
              >
                Start Your Free Agency Account
              </Button>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center text-blue-100 text-sm">
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                No credit card required
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                Free plan available
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                Setup in 5 minutes
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AgencyPage;